import "@/components/Interface/FormFields/ListBox/ListBox.scss";

import { Listbox, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, type ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Check, ChevronDown } from "@/assets/icons/icons";
import {
  LISTBOX_OPTIONS_MAX_HEIGHT,
  useOpenUpward,
} from "@/hooks/useOpenUpward";

const MAX_VISIBLE_TAGS = 2;

/** A single removable pill chip shown inside the multiselect trigger. */
const SelectionTag = ({
  label,
  onRemove,
  inverted,
}: {
  label: string;
  onRemove: (e: React.MouseEvent) => void;
  inverted?: boolean;
}) => (
  <span
    className={clsx(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
      inverted
        ? "bg-surface-secondary-inverse text-foreground-primary-inverse"
        : "bg-surface-primary text-foreground-primary-inverse",
    )}
  >
    {label}
    <button
      type="button"
      aria-label={`Remove ${label}`}
      className="flex items-center leading-none"
      onClick={onRemove}
    >
      ×
    </button>
  </span>
);

interface ListBoxMultipleContentProps<OptionType> {
  open: boolean;
  className?: string;
  label?: string;
  availableValues: OptionType[];
  disabled?: boolean;
  hideArrowWhenDisabled?: boolean;
  inverted?: boolean;
  showSelectedAsTags?: boolean;
  optionsHeader?: ReactNode | ((ctx: { open: boolean }) => ReactNode);
  renderOption: (
    option: OptionType,
    selected: boolean,
    active: boolean,
  ) => JSX.Element;
  valueLabel: ReactNode;
  tagsOverlay?: ReactNode;
}

// Split out from ListBoxMultiple so useOpenUpward (a hook) is called from a
// proper component, not from Headless UI's render-prop callback directly.
const ListBoxMultipleContent = <OptionType,>({
  open,
  className,
  label,
  availableValues,
  disabled,
  hideArrowWhenDisabled,
  inverted,
  showSelectedAsTags,
  optionsHeader,
  renderOption,
  valueLabel,
  tagsOverlay,
}: ListBoxMultipleContentProps<OptionType>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const openUpward = useOpenUpward(
    wrapperRef,
    open,
    LISTBOX_OPTIONS_MAX_HEIGHT,
  );

  return (
    <div
      ref={wrapperRef}
      className={clsx(
        "listbox-container",
        openUpward && "opens-upward",
        className,
      )}
    >
      <div className="relative">
        <Listbox.Button
          className={clsx(
            "listbox-button",
            (label || showSelectedAsTags) &&
              "!h-auto !min-h-14 !flex-col !items-start !justify-center gap-0.5",
            inverted &&
              "!bg-surface-tertiary-inverse !text-foreground-primary-inverse",
            disabled &&
              "pointer-events-none cursor-not-allowed !bg-surface-quaternary !text-foreground-tertiary",
          )}
        >
          {/* Small caption above the value */}
          {label && <span className="listbox-button__label">{label}</span>}
          {valueLabel}
        </Listbox.Button>
        {/* Tags are rendered outside Listbox.Button to avoid nested buttons. */}
        {tagsOverlay && (
          <div className="pointer-events-none absolute inset-0 flex items-center px-5 pr-14">
            <div className="pointer-events-auto flex flex-wrap gap-1">
              {tagsOverlay}
            </div>
          </div>
        )}
        {!(disabled && hideArrowWhenDisabled) && (
          <div className="icon-wrapper">
            <ChevronDown
              className={clsx(
                "icon",
                inverted && "!stroke-foreground-primary-inverse",
              )}
              aria-hidden="true"
            />
          </div>
        )}

        <Transition
          show={open}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="listbox-options">
            {typeof optionsHeader === "function"
              ? optionsHeader({ open })
              : optionsHeader}
            {availableValues?.map((column, index) => (
              <Listbox.Option
                key={index}
                className={({ active }) => `option ${active ? "active" : ""}`}
                value={column}
              >
                {({ selected, active }) => (
                  <>
                    {renderOption(column, selected, active)}

                    {selected && (
                      <Check
                        aria-hidden="true"
                        className={`icon selected ${active ? "active" : ""} `}
                      />
                    )}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </div>
  );
};

export const ListBoxMultiple = <OptionType,>({
  className,
  label,
  selected,
  setSelected,
  displayValue,
  availableValues,
  itemLabel,
  placeholder,
  disabled,
  hideArrowWhenDisabled,
  inverted,
  showAmountForMultipleSelection,
  showSelectedAsTags,
  optionsHeader,
  afterSelectionChange,
  renderSelectedValue,
  renderOption = (option: OptionType, selected: boolean) => (
    <span className={`text ${selected ? "selected" : ""}`}>
      {displayValue(option)}
    </span>
  ),
}: {
  className?: string;
  /** Small caption (e.g. "Rufnummer") shown above the selected value. */
  label?: string;
  selected: OptionType[] | undefined;
  setSelected: (value: OptionType[]) => void;
  displayValue: (value: OptionType) => string;
  availableValues: OptionType[];
  itemLabel: string;
  placeholder?: string;
  disabled?: boolean;
  hideArrowWhenDisabled?: boolean;
  /** Renders the trigger with dark/inverted colours for use on dark backgrounds. */
  inverted?: boolean;
  showAmountForMultipleSelection?: boolean;
  /**
   * Renders selected items as removable pill chips inside the trigger.
   * Up to two items are shown by name; any further items collapse into a "+N ×" chip.
   */
  showSelectedAsTags?: boolean;
  optionsHeader?: ReactNode | ((ctx: { open: boolean }) => ReactNode);
  /** Runs after the listbox value changes (e.g. refocus a filter field inside `optionsHeader`). */
  afterSelectionChange?: () => void;
  /**
   * Overrides the default collapsed "a, b (+1 selected)" text — e.g. to
   * render removable tags instead. Receives that default as `defaultValue`
   * so it can opt back into it for a given state (e.g. while `open`, the
   * trigger shows the plain collapsed text, not tags) — returning
   * `undefined` from this callback would render as empty, not as the
   * default, since the call site wraps it in JSX.
   */
  renderSelectedValue?: (
    value: OptionType[] | undefined,
    setSelected: (value: OptionType[]) => void,
    open: boolean,
    defaultValue: ReactNode,
  ) => ReactNode;
  renderOption?: (
    option: OptionType,
    selected: boolean,
    active: boolean,
  ) => JSX.Element;
}) => {
  const { t } = useTranslation();

  const translateMultipleValues: (value: OptionType[] | undefined) => any = (
    value = [],
  ) => {
    if (showAmountForMultipleSelection ? value.length > 1 : value.length > 3) {
      return (
        <>
          {`${itemLabel} `}
          <span className="font-semibold">{`(${value.length} ${t(
            "Common.selected",
          )})`}</span>
        </>
      );
    }

    return value.length > 0 ? value.map(displayValue).join(", ") : placeholder;
  };

  const renderTagsPlaceholder = (value: OptionType[] | undefined): ReactNode =>
    value?.length ? null : (
      <span
        className={
          inverted
            ? "text-foreground-tertiary-inverse"
            : "text-foreground-tertiary"
        }
      >
        {placeholder}
      </span>
    );

  const renderTagsOverlay = (value: OptionType[] | undefined): ReactNode => {
    if (!value?.length) {
      return null;
    }

    const visible = value.slice(0, MAX_VISIBLE_TAGS);
    const overflow = value.slice(MAX_VISIBLE_TAGS);

    return (
      <>
        {visible.map((item, i) => (
          <SelectionTag
            key={i}
            label={displayValue(item)}
            inverted={inverted}
            onRemove={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelected(value.filter((v) => v !== item));
              afterSelectionChange?.();
            }}
          />
        ))}
        {overflow.length > 0 && (
          <SelectionTag
            label={`+${overflow.length}`}
            inverted={inverted}
            onRemove={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelected(visible);
              afterSelectionChange?.();
            }}
          />
        )}
      </>
    );
  };

  return (
    <Listbox
      value={selected ? selected : []}
      onChange={(next) => {
        setSelected(next);
        afterSelectionChange?.();
      }}
      multiple
      disabled={disabled}
    >
      {({ open }) => {
        const defaultValueLabel = (
          <span
            className={clsx(
              "min-h-7 truncate",
              placeholder &&
                (selected?.length === 0 || !selected) &&
                (inverted
                  ? "text-foreground-tertiary-inverse"
                  : "text-foreground-tertiary"),
            )}
          >
            {translateMultipleValues(selected)}
          </span>
        );

        let valueLabel: ReactNode;
        let tagsOverlay: ReactNode;

        if (showSelectedAsTags) {
          valueLabel = renderTagsPlaceholder(selected);
          tagsOverlay = renderTagsOverlay(selected);
        } else if (renderSelectedValue) {
          valueLabel = renderSelectedValue(
            selected,
            setSelected,
            open,
            defaultValueLabel,
          );
        } else {
          valueLabel = defaultValueLabel;
        }

        return (
          <ListBoxMultipleContent<OptionType>
            open={open}
            className={className}
            label={label}
            availableValues={availableValues}
            disabled={disabled}
            hideArrowWhenDisabled={hideArrowWhenDisabled}
            inverted={inverted}
            showSelectedAsTags={showSelectedAsTags}
            optionsHeader={optionsHeader}
            renderOption={renderOption}
            valueLabel={valueLabel}
            tagsOverlay={tagsOverlay}
          />
        );
      }}
    </Listbox>
  );
};
